// Elintra — Canonical Fixture Data | UAG v3.0.0 Authoritative Values

import type {
  ProductFixture, StoreRecord, ProtocolHealthItem,
  PaymentRailHealthItem, ChartDataPoint, AgentQuery, Protocol,
} from '@/types/app';

export const CREATOR_AGENTS = [
  'claude-shopping-agent',  'gemini-cart-assistant',    'perplexity-buy-planner',
  'claude-creator-scout',   'gemini-deal-researcher',   'perplexity-checkout-copilot',
] as const;

export const CREATOR_INTENTS = [
  'notion content planner template', 'figma landing page kit',
  'email course for creators',       'newsletter monetization playbook',
  'cinematic video preset pack',     'lifestyle photo pack bundle',
] as const;

export const CREATOR_PRODUCTS: ProductFixture[] = [
  { id: 'prod-notion-system', name: 'Creator OS Notion System',      category: 'template',   price: 39 },
  { id: 'prod-figma-kit',     name: 'Launchpad Figma UI Kit',        category: 'design-kit', price: 49 },
  { id: 'prod-email-course',  name: 'Email Audience Mini-Course',    category: 'course',     price: 59 },
  { id: 'prod-newsletter-pb', name: 'Newsletter Growth Playbook',    category: 'playbook',   price: 79 },
  { id: 'prod-video-presets', name: 'Cinematic Creator Preset Pack', category: 'preset',     price: 29 },
  { id: 'prod-photo-pack',    name: 'Lifestyle Creator Photo Pack',  category: 'photo-pack', price: 24 },
];

export const STORE_FIXTURES: StoreRecord[] = [
  {
    id: 'store-stripe-primary', platform: 'Stripe',  name: 'Elintra Stripe Catalog',
    status: 'connected', lastSync: new Date(Date.now() - 1_000 * 60 * 18).toISOString(),
    productCount: 24, complianceScore: 94,
  },
  {
    id: 'store-shopify-main',    platform: 'Shopify', name: 'Preset Studio Store',
    status: 'syncing',   lastSync: new Date(Date.now() - 1_000 * 60 * 47).toISOString(),
    productCount: 18, complianceScore: 87,
  },
  {
    id: 'store-gumroad-digital', platform: 'Gumroad', name: 'Creator Goods Vault',
    status: 'attention', lastSync: new Date(Date.now() - 1_000 * 60 * 132).toISOString(),
    productCount: 31, complianceScore: 91,
  },
];

// v3.0.0: 5 protocol surfaces — A2A, ACP, UCP, NLIP + x402 (PAYMENT_LAYER)
export const PROTOCOL_HEALTH_FALLBACK: ProtocolHealthItem[] = [
  { id: 'a2a',  label: 'A2A',  owner: 'Google / Linux Foundation', status: 'Healthy',  endpoint: '/.well-known/a2a',  type: 'ROUTING_PROTOCOL' },
  { id: 'acp',  label: 'ACP',  owner: 'IBM / Linux Foundation',    status: 'Healthy',  endpoint: '/.well-known/acp',  type: 'ROUTING_PROTOCOL' },
  { id: 'ucp',  label: 'UCP',  owner: 'Commerce Ecosystem',        status: 'Preview',  endpoint: '/.well-known/ucp',  type: 'ROUTING_PROTOCOL' },
  { id: 'nlip', label: 'NLIP', owner: 'ECMA International',        status: 'Degraded', endpoint: '/.well-known/nlip', type: 'ROUTING_PROTOCOL' },
  {
    id:       'x402',
    label:    'x402',
    owner:    'Coinbase / x402 Foundation',
    status:   'Active',
    endpoint: '/.well-known/x402',
    type:     'PAYMENT_LAYER',
  },
];

// v3.0.0: 6 rails — Stripe + Algorand x402 both active (Phase 2A)
export const PAYMENT_RAILS_FALLBACK: PaymentRailHealthItem[] = [
  { id: 'stripe',         label: 'Stripe ACP',             status: 'active',  phase: 'Phase 1' },
  {
    id:           'algorand_x402',
    label:        'Algorand x402 (USDT-ASA)',
    status:       'active',
    address:      'elintra.algo',
    phase:        'Phase 2',
    network:      'algorand-mainnet',
    facilitator:  'GoPlausible',
    finalityMs:   4_000,
  },
  { id: 'btc_lightning',  label: 'Bitcoin Lightning',       status: 'ready',   address: 'elintra256@tether.me', phase: 'Phase 2' },
  { id: 'usdt_lightning', label: 'USDT Lightning',          status: 'ready',   address: 'elintra256@tether.me', phase: 'Phase 2' },
  { id: 'usdt_onchain',   label: 'USDT On-chain',           status: 'ready',   address: 'elintra256@tether.me', phase: 'Phase 2' },
  { id: 'xrpl_rlusd',     label: 'XRPL RLUSD (XLS-80)',     status: 'preview',                                   phase: 'Phase 3' },
];

export const AEO_QUERY_TABLE = [
  { q: 'notion content planner template',  v: 1245, m: 98,  c: 12.4, r: '4,520', trend: 'up'   },
  { q: 'figma landing page kit',           v: 843,  m: 100, c: 8.2,  r: '2,100', trend: 'up'   },
  { q: 'email course for creators',        v: 512,  m: 45,  c: 2.1,  r: '450',   trend: 'flat' },
  { q: 'cinematic video preset pack',      v: 420,  m: 0,   c: 0,    r: '0',     trend: 'down' },
  { q: 'newsletter monetization playbook', v: 384,  m: 88,  c: 6.7,  r: '1,240', trend: 'up'   },
  { q: 'lifestyle photo pack bundle',      v: 201,  m: 72,  c: 4.3,  r: '390',   trend: 'flat' },
] as const;

let counter = 0;

export const generateMockQuery = (): AgentQuery => {
  const i      = counter;
  const protocols: Protocol[] = ['A2A', 'ACP', 'UCP', 'NLIP'];
  const mod10      = i % 10;
  const isPurchase = mod10 <= 2;
  const isMatch    = mod10 >= 3 && mod10 <= 6;
  const product    = CREATOR_PRODUCTS[i % CREATOR_PRODUCTS.length];
  // v3.0.0: every 3rd purchase is settled via algorand_x402
  const isAlgorandPurchase = isPurchase && (i % 3 === 0);
  counter++;
  return {
    id:             `query-${i}-${Date.now()}`,
    agentId:        CREATOR_AGENTS[i % CREATOR_AGENTS.length],
    protocol:       protocols[i % protocols.length],
    queryIntent:    CREATOR_INTENTS[i % CREATOR_INTENTS.length],
    productMatched: (isPurchase || isMatch) ? product.name : null,
    outcome:        isPurchase ? 'PURCHASED' : isMatch ? 'MATCHED' : 'REJECTED',
    amount:         isPurchase ? product.price.toFixed(2) : null,  // decimal STRING
    latencyMs:      80 + ((i * 37) % 420),
    timestamp:      new Date().toISOString(),
    settlementRail: isAlgorandPurchase ? 'algorand_x402' : isPurchase ? 'stripe' : undefined,
    // v3.0.0: mock algorand_tx_id for x402 purchases — realistic 52-char Algorand TX format
    algorand_tx_id: isAlgorandPurchase
      ? `ALGO${(i * 0xdeadbeef).toString(16).toUpperCase().padStart(48, '0').slice(0, 48)}`
      : undefined,
    governanceHash: isPurchase
      ? Array.from({ length: 64 }, (_, k) => ((i + k) * 7).toString(16).slice(-1)).join('')
      : undefined,
  };
};

export const generateChartData = (): ChartDataPoint[] =>
  Array.from({ length: 30 }, (_, i): ChartDataPoint => {
    const a2a  = 80 + (i * 6) % ((i + 4) * 8 || 1);
    const acp  = 40 + (i * 5) % ((i + 3) * 6 || 1);
    const ucp  = 22 + (i * 3) % ((i + 5) * 4 || 1);
    const nlip = 12 + (i * 2) % ((i + 6) * 3 || 1);
    const queries     = a2a + acp + ucp + nlip;
    const conversions = Math.round(queries * 0.29);
    // v3.0.0: x402_payments = ~40% of conversions (ramps up over the 30-day window)
    const x402_ramp       = Math.min(1, i / 14);           // ramps from day 0→14 then plateaus
    const x402_payments   = Math.round(conversions * 0.40 * x402_ramp);
    // $0.05 per x402 payment, as a decimal STRING via integer-cents math (no float)
    const algoCents        = x402_payments * 5;
    const algo_volume_usdt = `${Math.trunc(algoCents / 100)}.${String(algoCents % 100).padStart(2, '0')}`;
    return {
      date: new Date(Date.now() - (29 - i) * 86_400_000)
              .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      A2A: a2a, ACP: acp, UCP: ucp, NLIP: nlip,
      queries,
      conversions,
      x402_payments,
      algo_volume_usdt,
    };
  });
